import { useState, useEffect, useCallback, useRef } from 'react';
import ReactQuill from 'react-quill';
import { MessagePlugin } from 'tdesign-react';
import { uploadApi } from '../api'; // 根据实际路径调整
import 'react-quill/dist/quill.snow.css';

// ===================== 图片上传处理 =====================
const handleQuillImageUpload = async (file: File): Promise<string | null> => {
  const url = await uploadApi.uploadImage(file);
  if (!url) {
    MessagePlugin.error('图片上传失败');
  }
  return url;
};

// ===================== Quill 配置 =====================
const quillModules = {
  toolbar: {
    container: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      ['link', 'image', 'video'],
      ['clean'],
    ],
    handlers: {
      image: function (this: any) {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/png, image/jpeg, image/jpg, image/gif');
        input.click();
        input.onchange = async () => {
          if (!input.files?.[0]) return;
          const file = input.files[0];
          if (file.size > 5 * 1024 * 1024) {
            MessagePlugin.error('图片大小不能超过5MB');
            return;
          }
          const quill = this.quill;
          const range = quill.getSelection(true);
          if (!range) return;
          const placeholder = '【图片上传中...】';
          quill.insertText(range.index, placeholder, 'italic', true);
          const imageUrl = await handleQuillImageUpload(file);
          const text = quill.getText();
          const placeholderIndex = text.indexOf(placeholder);
          const deleteIndex = placeholderIndex !== -1 ? placeholderIndex : range.index;
          const deleteLength = placeholderIndex !== -1 ? placeholder.length : 0;
          if (imageUrl) {
            quill.deleteText(deleteIndex, deleteLength);
            quill.insertEmbed(deleteIndex, 'image', imageUrl);
            quill.setSelection(deleteIndex + 1);
          } else {
            quill.deleteText(deleteIndex, deleteLength);
          }
        };
      },
    },
  },
  clipboard: { matchVisual: false },
};

const quillFormats = [
  'header', 'font', 'size', 'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent', 'link', 'image', 'video', 'code-block',
  'align', 'color', 'background', 'script', 'direction'
];

interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  showHeight?: boolean;
}

export default function ProductRichTextEditor({ value, onChange, readOnly, showHeight = true }: RichTextEditorProps) {
  const [editorValue, setEditorValue] = useState(value || '');
  const quillRef = useRef<ReactQuill>(null);
  const isInitialized = useRef(false);

  useEffect(() => {
    if (value !== undefined && value !== editorValue) setEditorValue(value);
  }, [value]);

  const handleChange = useCallback((content: string) => {
    setEditorValue(content);
    onChange?.(content);
  }, [onChange]);

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill || isInitialized.current) return;
    isInitialized.current = true;
    try {
      // 注意：这里需要确保 Delta 类型正确，如果报错请检查 tsconfig 或安装 @types/quill
      quill.clipboard.addMatcher('IMG', (node, delta) => {
        const src = (node as HTMLImageElement).getAttribute('src');
        if (src?.startsWith('data:image/')) {
          fetch(src).then(r => r.blob()).then(blob => {
            const file = new File([blob], 'paste-image.jpg', { type: blob.type });
            handleQuillImageUpload(file).then(url => {
              if (url && quillRef.current) {
                const editor = quillRef.current.getEditor();
                const range = editor.getSelection(true);
                if (range) {
                  editor.insertEmbed(range.index, 'image', url);
                  editor.setSelection(range.index + 1);
                }
              }
            });
          });
          // 修复 TypeScript 报错，返回空 Delta 实例
          return new (window as any).Delta(); 
        }
        return delta;
      });
    } catch (err) {
      console.warn('Quill matcher 注册失败:', err);
    }
    return () => { isInitialized.current = false; };
  }, []);

  return (
    <div className="border rounded" style={showHeight ? { height: 400 } : {}}>
      <ReactQuill
        ref={quillRef}
        value={editorValue}
        onChange={handleChange}
        modules={quillModules}
        formats={quillFormats}
        theme="snow"
        style={showHeight ? { height: '90%' } : {}}
        placeholder="请输入内容（支持图片上传）..."
        readOnly={readOnly}
      />
    </div>
  );
}